import type { WhatsAppConfig } from "../whatsappTypes";

/**
 * Almikia Epic EW2 (TASK-080): validation of the Direccion WhatsApp settings forms. The database
 * re-checks every bound (`whatsapp_config` / `whatsapp_templates` check constraints); this only
 * keeps a typo from reaching it and names the offending field in plain Spanish.
 */
export const GREETING_MAX_LENGTH = 1000;

export type ConfigFormValues = {
  windowDirectHours: string;
  ctwaFreeHours: string;
  monthlyFreeQuota: string;
  alert24hHoursLeft: string;
  alertCtwaDaysLeft: string;
  sendRateLimit: string;
  templatesEnabled: boolean;
  welcomeEnabled: boolean;
  welcomeName: string;
  welcomeLanguage: string;
  /** Comma-separated variable names, in the order of the template placeholders. */
  welcomeVariables: string;
  greeting: string;
};

export type ConfigField = keyof ConfigFormValues;

export const configToForm = (config: WhatsAppConfig): ConfigFormValues => ({
  windowDirectHours: String(config.window_direct_hours),
  ctwaFreeHours: String(config.ctwa_free_hours),
  monthlyFreeQuota: String(config.monthly_free_quota),
  alert24hHoursLeft: String(config.alert_24h_hours_left),
  alertCtwaDaysLeft: String(config.alert_ctwa_days_left),
  sendRateLimit: String(config.send_rate_limit_per_minute),
  templatesEnabled: config.templates_enabled,
  welcomeEnabled: config.welcome_template_enabled,
  welcomeName: config.welcome_template_name ?? "",
  welcomeLanguage: config.welcome_template_language,
  welcomeVariables: (config.welcome_template_variables ?? []).join(", "),
  greeting: config.button_lead_greeting,
});

/** A whole number of at least `min`; anything else (empty, decimal, text) is `null`. */
export const parseWholeNumber = (text: string, min: number): number | null => {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value >= min ? value : null;
};

/** Comma-separated names into a clean list (no blanks, no duplicates, order kept). */
export const parseVariables = (text: string): string[] =>
  Array.from(
    new Set(
      text
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean),
    ),
  );

export type ConfigParseResult =
  | { ok: true; patch: Omit<WhatsAppConfig, "id"> }
  | { ok: false; fields: ConfigField[] };

export const parseConfigForm = (
  values: ConfigFormValues,
): ConfigParseResult => {
  const windowDirect = parseWholeNumber(values.windowDirectHours, 1);
  const ctwaFree = parseWholeNumber(values.ctwaFreeHours, 1);
  const quota = parseWholeNumber(values.monthlyFreeQuota, 0);
  const alert24 = parseWholeNumber(values.alert24hHoursLeft, 1);
  const alertCtwa = parseWholeNumber(values.alertCtwaDaysLeft, 1);
  const rate = parseWholeNumber(values.sendRateLimit, 1);
  const fields: ConfigField[] = [];
  if (windowDirect == null) fields.push("windowDirectHours");
  if (ctwaFree == null) fields.push("ctwaFreeHours");
  if (quota == null) fields.push("monthlyFreeQuota");
  if (alert24 == null) fields.push("alert24hHoursLeft");
  if (alertCtwa == null) fields.push("alertCtwaDaysLeft");
  if (rate == null) fields.push("sendRateLimit");
  if (values.greeting.length > GREETING_MAX_LENGTH) fields.push("greeting");
  if (!values.welcomeLanguage.trim()) fields.push("welcomeLanguage");
  if (values.welcomeEnabled && !values.welcomeName.trim()) {
    fields.push("welcomeName");
  }
  if (
    fields.length > 0 ||
    windowDirect == null ||
    ctwaFree == null ||
    quota == null ||
    alert24 == null ||
    alertCtwa == null ||
    rate == null
  ) {
    return { ok: false, fields };
  }
  return {
    ok: true,
    patch: {
      window_direct_hours: windowDirect,
      ctwa_free_hours: ctwaFree,
      monthly_free_quota: quota,
      alert_24h_hours_left: alert24,
      alert_ctwa_days_left: alertCtwa,
      send_rate_limit_per_minute: rate,
      templates_enabled: values.templatesEnabled,
      welcome_template_enabled: values.welcomeEnabled,
      welcome_template_name: values.welcomeName.trim() || null,
      welcome_template_language: values.welcomeLanguage.trim(),
      welcome_template_variables: parseVariables(values.welcomeVariables),
      button_lead_greeting: values.greeting,
    },
  };
};

export type TemplateFormValues = {
  name: string;
  language: string;
  label: string;
  variables: string;
  isPaid: boolean;
};

export type TemplateField = "name" | "language" | "label";

const TEMPLATE_NAME = /^[a-z0-9_]+$/;
const TEMPLATE_LANGUAGE = /^[a-z]{2}(_[A-Z]{2})?$/;

/** The invalid fields of a catalogue entry: Meta template names are lowercase, digits and `_`. */
export const validateTemplateForm = (
  values: TemplateFormValues,
): TemplateField[] => {
  const fields: TemplateField[] = [];
  if (!TEMPLATE_NAME.test(values.name.trim())) fields.push("name");
  if (!TEMPLATE_LANGUAGE.test(values.language.trim())) fields.push("language");
  if (!values.label.trim()) fields.push("label");
  return fields;
};
